import React, { useState, useContext, useEffect, useReducer } from 'react';
import QuotesGrid from './QuotesGrid';
import { reservedTextStyle, airtimeReservationTextStyle } from '../AirtimePlanning/AirtimePlanningStyles';
import ReservationTimer from '../../generic/ReservationTimer';
import {
    cancelMessageButtonStyle,
    okMessageButtonStyle,
    pageContainerStyle,
    sectionedPanelStyle
} from '../CampaignStyles';
import { BusinessTypes, QuoteStatusEnum } from '../../../constants/Enums';
import { AirtimePlanningContext } from '../../../contexts/AirtimePlanningContext';
import {
    CreateQuote,
    GetCampaignSettings,
    GetQuotes,
    GetQuoteType,
    SendQuoteToJet,
    CheckingQuotePriceAgainstBalances
} from '../../../services/CampaignsDataService';
import { AsButton, getClaimValue, useModal } from '@adserve/adserve-react-components';
import { apiParamsReducer } from '../../../services/AsGridService';
import CreateQuoteModal from './CreateQuoteModal';
import AsSelectWithPlaceholder from '../../generic/AsSelectWithPlaceholder';
import { GetWorkflowTeams, UsingDynamicWorkflow } from '../../../services/WorkflowService';
import { GetUser } from '../../../services/DataService';
import moment from 'moment';
import { withOutTimeZoneDate } from '../../../services/DateService';

const Quote1headerStyle = {
    color: '#414141',
    fontSize: 17,
    fontWeight: 'bold',
    fontFamily: 'Roboto'
};

let mounted = false;

const QuoteAndBooking = ({
    campaign,
    billingAccountCreditDetails,
    getCampaign,
    changeMade,
    updateCampaign,
    readOnly = false,
    campaignBurst,
    getCampaignBurst,
    activeRequest = {},
    discountRequest = {},
    dealDiscounts = [],
    onDiscountRequestApproval,
    discountApprovalWorkflowId = undefined,
    valuePotRequest = {},
    valuePotApprovalWorkflowId = undefined,
    isBurst = false
}) => {
    const { addMessage, removeMessage } = useModal();
    const [quoteType, setQuoteType] = useState('');
    const [workflowTeams, setWorkflowTeams] = useState([]);
    const [isUsingEatWorkflow, setIsUsingEatWorkflow] = useState(false);
    const [isUsingDynamicWorkflow, setIsUsingDynamicWorkflow] = useState(false);
    const [isReserved, setIsReserved] = useState(true);
    const [userPAT, setUserPAT] = useState();
    const [isLoading, setIsLoading] = useState(false);
    const {
        currentPlan,
        setCurrentPlan,
        setSelectedProperties,
        setReservedUntil,
        canQuote,
        hasAirtimeExpired,
        offairActivities,
        setOffairActivities,
        quotes,
        setQuotes,
        planIsInvalid,
        billingAccountChanged,
        setBillingAccountChanged
    } = useContext(AirtimePlanningContext);

    const apiParamDefaults = {
        pageNumber: 1,
        pageSize: 15,
        sortField: 'createdDate',
        sortOrder: 'desc'
    };
    const [apiParams, apiParamsDispatcher] = useReducer(apiParamsReducer, apiParamDefaults);

    const refreshCampaign = async () => {
        setIsLoading(true);
        await getCampaign(campaign.id);
        if (isBurst) {
            await getCampaignBurst(campaignBurst.id);
            if (!planIsInvalid) {
                await setCurrentPlan(campaignBurst.currentPlan);
            }
        } else {
            if (!planIsInvalid) {
                await setCurrentPlan(campaign.currentPlan);
            }
        }
        setOffairActivities(campaign.offairActivities);
        setIsLoading(false);
    };

    const getStatusName = () => {
        if (campaignBurst !== null && campaignBurst !== undefined) {
            let statusName = campaignBurst.currentPlan?.quote?.statusName
                ? 'Airtime is ' + campaignBurst.currentPlan?.quote?.statusName?.toLowerCase() + '.'
                : 'Airtime has expired.';
            if (campaignBurst.currentPlan?.quote?.status === 5 && !isReserved) statusName = 'Airtime has expired.';
            return statusName;
        }

        let statusName = campaign?.currentPlan?.quote?.statusName
            ? 'Airtime is ' + campaign?.currentPlan?.quote?.statusName?.toLowerCase() + '.'
            : 'Airtime has expired.';
        if (campaign?.currentPlan?.quote?.status === 5 && !isReserved) statusName = 'Airtime has expired.';
        return statusName;
    };

    const getQuotes = async () => {
        if (!campaign.id) return;
        setIsLoading(true);
        try {
            const response = await GetQuotes(campaign.id, campaignBurst?.id, apiParams);
            if (!mounted) {
                return;
            }
            setQuotes(
                response.data.map((d) => ({
                    ...d,
                    book: d.status === 1 || d.status === 5 ? 'Book' : 'Booked',
                    bookDisabled: d.status !== 1 && d.status !== 5,
                    algorithm: 'Heuristic'
                }))
            );
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error(`Error retrieving quotes\n${err?.message || err}`);
            addMessage({
                message: window.translate('An error occured while retriving quotes.'),
                buttons: [
                    {
                        text: window.translate('Ok'),
                        callback: null
                    }
                ]
            });
        } finally {
            setIsLoading(false);
        }
    };

    const getData = async () => {
        setIsLoading(true);
        const settingsTask = GetCampaignSettings();
        const workflowTeamsTask = GetWorkflowTeams();
        const quoteTypeTask = GetQuoteType(campaign.id, campaignBurst?.id);
        const settings = await settingsTask;
        const workflowTeamsData = await workflowTeamsTask;
        const qt = await quoteTypeTask;

        const workflowIds = [];

        // Bursts aren't included in workflow but this will need updating if they start using it
        if (campaign.businessType.toLowerCase() === BusinessTypes.SANDP.name.toLowerCase()) {
            if (settings.sandPPreBookingWorkflowId) {
                workflowIds.push(settings.sandPPreBookingWorkflowId);
            }

            if (settings.sandPPostBookingWorkflowId) {
                workflowIds.push(settings.sandPPostBookingWorkflowId);
            }
        } else {
            if (settings.preBookingWorkflowId) {
                workflowIds.push(settings.preBookingWorkflowId);
            }

            if (settings.postBookingWorkflowId) {
                workflowIds.push(settings.postBookingWorkflowId);
            }
        }

        let isEatWorkflow = false;
        let isDynamicWorkflow = false;
        if (workflowIds.length > 0) {
            const dynamicWorkflowTask = UsingDynamicWorkflow(workflowIds);
            const result = await dynamicWorkflowTask;
            if (result !== 0) {
                isEatWorkflow = result === 2;
                isDynamicWorkflow = true;
            }
        }

        if (!mounted) {
            return;
        }

        if (!planIsInvalid) {
            if (isBurst) {
                setCurrentPlan(campaignBurst.currentPlan);
            } else {
                setCurrentPlan(campaign.currentPlan);
            }
        }

        setOffairActivities(campaign.offairActivities);

        if (Array.isArray(campaign.selectedProperties)) {
            setSelectedProperties(campaign.selectedProperties);
        }

        setWorkflowTeams(workflowTeamsData);
        setIsUsingEatWorkflow(isEatWorkflow);
        setIsUsingDynamicWorkflow(isDynamicWorkflow);
        setQuoteType(qt);
        setIsLoading(false);
    };

    useEffect(() => {
        getQuotes();
    }, [isReserved === false, campaign, campaignBurst, apiParams]);

    useEffect(() => {
        mounted = true;
        getData();
        return () => {
            mounted = false;
        };
    }, []);

    useEffect(() => {
        if (!userPAT) {
            GetUser(getClaimValue('ASId')).then((data) => {
                if (data?.primaryApprovalTeamId) {
                    setUserPAT(data.primaryApprovalTeamId);
                } else {
                    setUserPAT(window.emptyGuid);
                }
            });
        }

        if (userPAT && userPAT !== window.emptyGuid) {
            if (!campaign.campaignApprovalTeamId) {
                changeMade('campaignApprovalTeamId', userPAT);
            }
        }

        if (campaign.campaignApprovalTeamId === '-1') {
            changeMade('campaignApprovalTeamId', undefined);
        }
    }, [campaign.campaignApprovalTeamId, userPAT]);

    useEffect(() => {
        if (campaignBurst !== null && campaignBurst !== undefined) {
            if (campaignBurst.currentPlan?.quote) {
                setIsReserved(campaignBurst.currentPlan.quote.status === 5);
            } else {
                // The plan has no quote so assume this can be reserved
                // (also covers case where we switch campaign versions on this page)
                setIsReserved(true);
            }
        } else {
            if (campaign.currentPlan?.quote) {
                setIsReserved(campaign.currentPlan.quote.status === 5);
            } else {
                // The plan has no quote so assume this can be reserved
                // (also covers case where we switch campaign versions on this page)
                setIsReserved(true);
            }
        }
    }, [campaign.currentPlan, campaignBurst?.currentPlan]);

    const createQuote = async (name, quoteFormat, includeSignatureBlock) => {
        setIsReserved(true);
        setIsLoading(true);
        if (currentPlan) {
            currentPlan.quoted = true;
        }
        const quoteRequest = {
            name,
            planId: currentPlan?.id,
            campaignId: campaign.id,
            campaignBurstId: campaignBurst?.id,
            quoteFormat,
            includeSignatureBlock
        };
        if (!quoteRequest?.hasOwnProperty('burstIds')) {
            quoteRequest.burstIds = [];
        }
        try {
            const quote = await CreateQuote(quoteRequest);
            if (currentPlan && mounted) {
                const revDate = moment(quote?.reservedUntil);
                currentPlan.reservedUntil = revDate;
                currentPlan.quoteId = quote?.id;
                currentPlan.quote = quote;
                setReservedUntil(currentPlan.reservedUntil);
                setCurrentPlan(currentPlan);
            }
            await getQuotes();
            removeMessage();
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error(`quote creation failed\n${err?.message || err}`);
            addMessage({
                message: `${window.translate('An error has occured during quote creation.')} ${err.response?.data}`,
                buttons: [
                    {
                        text: window.translate('Ok'),
                        callback: null
                    }
                ]
            });
        } finally {
            setIsLoading(false);
        }
    };

    const sendToJet = async () => {
        setIsLoading(true);

        const localCampaign = campaignBurst ?? campaign;

        if (
            localCampaign.campaignStatusName === 'Booked' ||
            localCampaign.campaignStatusName === 'Running' ||
            localCampaign.campaignStatusName === 'Completed'
        ) {
            const quote = quotes.filter((x) => x.status === QuoteStatusEnum.BOOKED);

            try {
                await SendQuoteToJet(quote[0].id, 1);
            } catch (error) {
                addMessage({
                    message: `An error has occured while sending rict to J-ET: ${
                        error?.response?.data?.title || error
                    }`,
                    buttons: [{ text: window.translate('OK') }]
                });
                return;
            } finally {
                setIsLoading(false);
            }
        } else if (localCampaign.campaignStatusName === 'Cancelled') {
            const quote = quotes.filter((x) => x.status === QuoteStatusEnum.CANCELLED);

            try {
                await SendQuoteToJet(quote[0].id, 2);
            } catch (error) {
                addMessage({
                    message: `An error has occured while sending rict to J-ET: ${
                        error?.response?.data?.title || error
                    }`,
                    buttons: [{ text: window.translate('OK') }]
                });
                return;
            } finally {
                setIsLoading(false);
            }
        } else {
            const quote = quotes.filter(
                (x) => x.status === QuoteStatusEnum.PLANNED || x.status === QuoteStatusEnum.RESERVED
            );

            try {
                await SendQuoteToJet(quote[0].id, 0);
            } catch (error) {
                addMessage({
                    message: `An error has occured while sending rict to J-ET: ${
                        error?.response?.data?.title || error
                    }`,
                    buttons: [{ text: window.translate('OK') }]
                });
                return;
            } finally {
                setIsLoading(false);
            }
            getQuotes();
        }

        addMessage({
            message:
                localCampaign.campaignStatusName === 'Booked' ||
                localCampaign.campaignStatusName === 'Completed' ||
                localCampaign.campaignStatusName === 'Cancelled'
                    ? 'Booking resent successfully'
                    : 'Quote sent successfully',
            buttons: [{ text: window.translate('OK') }]
        });
    };

    const handleSendToJetClick = async () => {
        addMessage({ message: window.translate('Sending to J-ET...') });
        await sendToJet();
    };

    // Has been commented as this will be implemented once we get the account balances from SAP TODO
    // const newQuoteTotal = () => {
    //     let airtimeTotal = quoteType.toLowerCase() !== 'offair' ? (campaignBurst ? campaignBurst.budget : campaign.budget ?? 0) : 0;
    //     airtimeTotal -= campaign.valuePotSpend ?? 0;
    //     const offairTotal = campaign.offairActivities?.filter((x) => x.status !== 5).reduce((total, oa) => total + oa.price, 0) ?? 0;
    //     const total = airtimeTotal + offairTotal ?? 0;
    //     return total;
    // };

    const handleGenerateQuoteClick = async () => {
        if (isBurst) {
            const response = await CheckingQuotePriceAgainstBalances(
                campaign?.id,
                campaign?.businessType.toLowerCase(),
                campaignBurst?.id,
                isBurst,
                currentPlan?.id
            );
            // if response is NOT an empty string, quote cannot be booked
            if (response.length > 0) {
                addMessage({
                    message: window.translate(response),
                    buttons: [
                        {
                            text: window.translate('Ok'),
                            style: { width: 87 },
                            callback: null
                        }
                    ]
                });
                return;
            }
        }

        if (!campaign.modified) {
            await generateQuote();
            return;
        }

        addMessage({
            message: window.translate(
                'You have unsaved changes.  These need to be saved before creating a quote. Do you wish to save your changes now?'
            ),
            buttons: [
                {
                    text: window.translate('No'),
                    style: { ...cancelMessageButtonStyle, width: 132 }
                },
                {
                    text: window.translate('Yes'),
                    style: okMessageButtonStyle,
                    callback: async () => {
                        setBillingAccountChanged(false);
                        const result = await updateCampaign();

                        if (result) {
                            await generateQuote();
                        }
                    }
                }
            ]
        });
    };

    const generateQuote = async () => {
        if (quoteType?.toLowerCase()?.includes('sandp') && !campaignBurst) {
            // Don't show modal
            addMessage({ message: window.translate('Generating S&P Quote...') });
            await createQuote(`${campaign.name} S&P Quote ${moment().format('YYYY/MM/DD hh:mm:ss')}`);
            refreshCampaign();
            return;
        }

        // Show quote modal
        addMessage({
            modalStyle: {},
            message: (
                <CreateQuoteModal
                    campaign={campaign}
                    campaignBurst={campaignBurst}
                    setIsReserved={setIsReserved}
                    quoteType={quoteType}
                    offairActivities={offairActivities}
                    setReservedUntil={setReservedUntil}
                    createQuote={createQuote}
                    planId={currentPlan?.id}
                />
            )
        });
    };

    const handleNameChange = (quote) => {
        const mutar = [...quotes];
        const i = mutar.findIndex((q) => q.id === quote.id);
        mutar[i] = { ...quote };
        setQuotes(mutar);
    };

    const handleApprovalTeamChange = (e) => {
        changeMade('campaignApprovalTeamId', e.target.value);
    };

    const validateBooking = () => {
        if (campaignBurst) {
            // The parent S&P campaign must be booked before we can book a burst
            if (campaign.campaignStatusName !== 'Booked' && campaign.campaignStatusName !== 'Running') {
                addMessage({
                    message: window.translate(
                        'This burst cannot be booked because the parent S&P campaign is not yet booked. Please save your changes and book the S&P campaign first.'
                    ),
                    buttons: [
                        {
                            text: window.translate('Ok'),
                            style: { width: 87 },
                            callback: null
                        }
                    ]
                });
                return false;
            }
        }
        if (billingAccountCreditDetails?.creditStatusShortRef !== 'A') {
            const message = 'Cannot book this quote as the customer is not credit-approved';
            addMessage({
                message: window.translate(message),
                buttons: [
                    {
                        text: window.translate('Ok'),
                        style: { width: 87 },
                        callback: null
                    }
                ]
            });
            return;
        }

        return true;
    };

    const hasBeenBooked = () => {
        if (campaignBurst) {
            return (
                campaignBurst?.campaignStatusName === 'Booked' ||
                campaignBurst?.campaignStatusName === 'Running' ||
                campaignBurst?.campaignStatusName === 'Completed' ||
                campaignBurst?.campaignStatusName === 'Cancelled'
            );
        }

        return (
            campaign?.campaignStatusName === 'Booked' ||
            campaign?.campaignStatusName === 'Running' ||
            campaign?.campaignStatusName === 'Completed' ||
            campaign?.campaignStatusName === 'Cancelled'
        );
    };

    const getReserveUntil = () => {
        let revDate =
            currentPlan?.reservedUntil instanceof Date
                ? currentPlan?.reservedUntil.getFullYear() !== 1
                    ? currentPlan?.reservedUntil
                    : undefined
                : moment(currentPlan?.reservedUntil);
        if (revDate && currentPlan?.quote?.approvalStatus === 0) {
            revDate = withOutTimeZoneDate(revDate);
        }
        return revDate;
    };

    return (
        <div style={pageContainerStyle}>
            <div
                style={{
                    ...sectionedPanelStyle,
                    padding: '11.5px 24px 12px 24px'
                }}
            >
                <div
                    style={{
                        display: 'grid',
                        gridTemplateColumns: 'max-content auto max-content'
                    }}
                >
                    <span style={Quote1headerStyle}>{window.translate('Quote & Booking')}</span>
                    {campaign?.businessType.toLowerCase() === BusinessTypes.AIRTIME.name.toLowerCase() ? (
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center'
                            }}
                        >
                            <div>
                                <span style={airtimeReservationTextStyle}>
                                    {window.translate('Airtime reservation status : ')}
                                </span>
                                {campaign?.currentPlan?.quote?.status === 2 ? (
                                    <span style={reservedTextStyle('#199001')}>
                                        {window.translate('Airtime is booked.')}
                                    </span>
                                ) : isReserved ? (
                                    <ReservationTimer
                                        // Handling an empty plan (edge case where a plan has been invalidated and saved)
                                        reservedUntil={getReserveUntil()}
                                        stopTicking={false}
                                        isMounted={mounted}
                                        isReserved={isReserved}
                                        setIsReserved={setIsReserved}
                                    ></ReservationTimer>
                                ) : (
                                    <span
                                        style={reservedTextStyle(
                                            campaign?.currentPlan?.quote?.status < 4 ? '#199001' : '#DB0000'
                                        )}
                                    >
                                        {window.translate(getStatusName())}
                                    </span>
                                )}
                            </div>
                        </div>
                    ) : campaignBurst !== null && campaignBurst !== undefined ? (
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center'
                            }}
                        >
                            <div>
                                <span style={airtimeReservationTextStyle}>
                                    {window.translate('Airtime reservation status : ')}
                                </span>
                                {campaignBurst?.currentPlan?.quote?.status === 2 ? (
                                    <span style={reservedTextStyle('#199001')}>
                                        {window.translate('Airtime is booked.')}
                                    </span>
                                ) : isReserved ? (
                                    <ReservationTimer
                                        // Handling an empty plan (edge case where a plan has been invalidated and saved)
                                        reservedUntil={
                                            campaignBurst?.currentPlan?.reservedUntil instanceof Date
                                                ? campaignBurst?.currentPlan?.reservedUntil.getFullYear() !== 1
                                                    ? campaignBurst?.currentPlan?.reservedUntil
                                                    : undefined
                                                : undefined
                                        }
                                        stopTicking={false}
                                        isMounted={mounted}
                                        isReserved={isReserved}
                                        setIsReserved={setIsReserved}
                                    ></ReservationTimer>
                                ) : (
                                    <span
                                        style={reservedTextStyle(
                                            campaignBurst?.currentPlan?.quote?.status < 4 ? '#199001' : '#DB0000'
                                        )}
                                    >
                                        {window.translate(getStatusName())}
                                    </span>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div id='airtime-res-placeholder' />
                    )}
                    <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                        {(campaignBurst === null || campaignBurst === undefined) &&
                            isUsingEatWorkflow &&
                            userPAT &&
                            userPAT === window.emptyGuid && (
                                <AsSelectWithPlaceholder
                                    id='campaign-approval-team'
                                    name='campaignApprovalTeamId'
                                    values={workflowTeams}
                                    value={campaign.campaignApprovalTeamId}
                                    onChange={handleApprovalTeamChange}
                                    disabled={!canQuote(campaign) || readOnly}
                                    title={window.translate('Booking Approval Team')}
                                />
                            )}
                        {(campaign?.jcn || campaignBurst?.jcn) && (
                            <AsButton
                                id='send-to-jet'
                                handleClick={handleSendToJetClick}
                                disabled={
                                    isLoading ||
                                    (hasBeenBooked() &&
                                        !quotes.some(
                                            (x) =>
                                                x.status === QuoteStatusEnum.BOOKED ||
                                                x.status === QuoteStatusEnum.CANCELLED
                                        )) ||
                                    (!hasBeenBooked() &&
                                        !quotes.some(
                                            (x) =>
                                                x.status === QuoteStatusEnum.PLANNED ||
                                                x.status === QuoteStatusEnum.RESERVED
                                        ))
                                }
                                style={{
                                    fontWeight: 'bold',
                                    fontSize: 15,
                                    height: 40,
                                    width: 130,
                                    gridRow: 'span 2',
                                    marginLeft: 20
                                }}
                            >
                                {window.translate(hasBeenBooked() ? 'Resend booking to J-ET' : 'Send to J-ET')}
                            </AsButton>
                        )}
                        <AsButton
                            id='quotes-booking-generate-quote-btn'
                            handleClick={handleGenerateQuoteClick}
                            disabled={
                                !billingAccountChanged &&
                                (!canQuote(campaignBurst ?? campaign) ||
                                (campaign.businessType.toLowerCase() !== BusinessTypes.SANDP.name.toLowerCase() &&
                                    hasAirtimeExpired()) ||
                                readOnly ||
                                isLoading ||
                                (quoteType === '' && campaign?.campaignTypeName?.toLowerCase() !== 'sandp'))
                            }
                            style={{
                                fontWeight: 'bold',
                                fontSize: 15,
                                height: 40,
                                width: 130,
                                gridRow: 'span 2',
                                marginLeft: 20
                            }}
                        >
                            {window.translate('Create Quote')}
                        </AsButton>
                    </div>
                </div>
                <QuotesGrid
                    campaign={campaign}
                    billingAccountCreditDetails={billingAccountCreditDetails}
                    refreshCampaign={refreshCampaign}
                    readOnly={readOnly}
                    isReserved={isReserved}
                    setIsReserved={setIsReserved}
                    quotes={quotes}
                    getQuotes={getQuotes}
                    onNameChange={handleNameChange}
                    apiParamsDispatcher={apiParamsDispatcher}
                    campaignApprovalTeam={campaign.campaignApprovalTeamId}
                    isCampaignModified={campaign.modified}
                    updateCampaign={updateCampaign}
                    businessType={campaignBurst ? campaignBurst.businessType : campaign.businessType}
                    validateBooking={validateBooking}
                    isCampaignBurst={!!campaignBurst}
                    activeRequest={activeRequest}
                    discountRequest={discountRequest}
                    dealDiscounts={dealDiscounts}
                    onDiscountRequestApproval={onDiscountRequestApproval}
                    isUsingDynamicWorkflow={isUsingDynamicWorkflow}
                    discountApprovalWorkflowId={discountApprovalWorkflowId}
                    valuePotRequest={valuePotRequest}
                    valuePotApprovalWorkflowId={valuePotApprovalWorkflowId}
                />
            </div>

            <div style={{ height: 16 }} />
        </div>
    );
};

export default QuoteAndBooking;
